<template>
  <div class="qr-link-actions">
    <!-- QR Code display -->
    <div v-if="link && isValidLink" class="qr-code-container text-center">
      <v-img
        v-if="qrCodeDataUrl"
        :src="qrCodeDataUrl"
        :width="size"
        :height="size"
        contain
        class="mx-auto"
      ></v-img>
      <v-progress-circular
        v-else-if="loading"
        indeterminate
        color="primary"
        class="mt-4"
      ></v-progress-circular>
      <v-alert v-else-if="error" type="error" dense text class="mt-2">
        {{ error }}
      </v-alert>
    </div>

    <!-- Placeholder si pas de lien -->
    <div v-else class="qr-placeholder text-center mb-4">
      <v-sheet
        :width="size"
        :height="size"
        color="grey lighten-4"
        class="mx-auto d-flex align-center justify-center"
      >
        <div class="text-center">
          <v-icon large color="grey">mdi-qrcode</v-icon>
          <div class="caption grey--text mt-2">{{ $t('qr.link-unavailable') }}</div>
        </div>
      </v-sheet>
    </div>

    <!-- Actions buttons -->
    <div class="qr-actions d-flex justify-center gap-2">
      <!-- Open link button -->
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            small
            color="primary"
            :disabled="!isValidLink"
            @click="openLink"
            v-bind="attrs"
            v-on="on"
            :aria-label="$t('qr.open-link')"
          >
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
        </template>
        <span>{{ isValidLink ? $t('qr.open-link') : $t('qr.link-unavailable') }}</span>
      </v-tooltip>

      <!-- Copy link button -->
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            small
            color="primary"
            :disabled="!isValidLink"
            @click="copyLink"
            v-bind="attrs"
            v-on="on"
            :aria-label="$t('qr.copy-link')"
          >
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </template>
        <span>{{ isValidLink ? $t('qr.copy-link') : $t('qr.link-unavailable') }}</span>
      </v-tooltip>

      <!-- Download QR button -->
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            small
            color="primary"
            :disabled="!isValidLink"
            @click="downloadQr"
            v-bind="attrs"
            v-on="on"
            :aria-label="$t('qr.download-qr')"
          >
            <v-icon small>mdi-download</v-icon>
          </v-btn>
        </template>
        <span>{{ isValidLink ? $t('qr.download-qr') : $t('qr.link-unavailable') }}</span>
      </v-tooltip>
    </div>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      bottom
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import QrCodeService from '@service/QrCodeService'

export default {
  name: 'QrLinkActions',

  props: {
    link: {
      type: String,
      default: null
    },
    size: {
      type: Number,
      default: 256
    },
    compact: {
      type: Boolean,
      default: false
    },
    showRefresh: {
      type: Boolean,
      default: false
    },
    showLink: {
      type: Boolean,
      default: false
    },
    filename: {
      type: String,
      default: 'payment-qrcode.png'
    }
  },

  data() {
    return {
      qrCodeDataUrl: null,
      loading: false,
      error: null,
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },

  computed: {
    isValidLink() {
      return QrCodeService.isValidLink(this.link)
    }
  },

  watch: {
    link: {
      immediate: true,
      handler(newLink) {
        if (newLink) {
          this.generateQrCode()
        } else {
          this.qrCodeDataUrl = null
        }
      }
    }
  },

  methods: {
    generateQrCode() {
      if (!this.isValidLink) {
        this.qrCodeDataUrl = null
        return
      }

      this.loading = true
      this.error = null

      QrCodeService.generateQrCode(this.link, this.size)
        .then(dataUrl => {
          this.qrCodeDataUrl = dataUrl
        })
        .catch(err => {
          this.error = this.$t('qr.generation-error')
        })
        .finally(() => {
          this.loading = false
        })
    },

    openLink() {
      if (!this.isValidLink) return
      window.open(this.link, '_blank')
    },

    copyLink() {
      if (!this.isValidLink) return

      QrCodeService.copyToClipboard(this.link)
        .then(() => {
          this.showSnackbar(this.$t('qr.link-copied'), 'success')
        })
        .catch(err => {
          this.showSnackbar(this.$t('qr.copy-error'), 'error')
        })
    },

    downloadQr() {
      if (!this.isValidLink || !this.qrCodeDataUrl) return

      try {
        QrCodeService.downloadQrCode(this.qrCodeDataUrl, this.filename)
        this.showSnackbar(this.$t('qr.qr-downloaded'), 'success')
      } catch (err) {
        this.showSnackbar(this.$t('qr.download-error'), 'error')
      }
    },

    refreshQr() {
      this.generateQrCode()
      this.showSnackbar(this.$t('qr.qr-refreshed'), 'success')
    },

    showSnackbar(message, color = 'success') {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    }
  }
}
</script>

<style scoped>
.qr-link-actions {
  width: 100%;
}

.qr-code-container,
.qr-placeholder {
  padding: 8px;
}

.qr-actions {
  gap: 8px;
}
</style>
